// A voice memo: round play button and the recording's own waveform, which
// fills with the accent color as it plays. `wave` is 1–9 bar heights saved at
// record time; without one, a gentle placeholder shape is drawn.
import { useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { ACCENT, BG, DIM, FG, MONO, alpha } from "../theme";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const PLACEHOLDER = Array.from({ length: 48 }, (_, i) => 2 + Math.round(4 * Math.abs(Math.sin(i * 0.55) * Math.cos(i * 0.21))));

const VoiceNote = ({ src, wave, durS, size = "md", color = ACCENT }) => {
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(durS || 0);
  const bars = wave && wave.length ? wave : PLACEHOLDER;
  const p = dur ? Math.min(1, t / dur) : 0;
  const h = size === "lg" ? 56 : 40;

  useEffect(() => {
    const a = audio.current;
    if (!a) return undefined;
    const onTime = () => setT(a.currentTime);
    const onMeta = () => Number.isFinite(a.duration) && setDur(a.duration);
    const onEnd = () => {
      setPlaying(false);
      setT(0);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const a = audio.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
    setPlaying(!playing);
  };

  const seek = (e) => {
    const a = audio.current;
    if (!a || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - r.left) / r.width) * dur;
    setT(a.currentTime);
  };

  return (
    <Flex align="center" gap={3} w="100%" onClick={(e) => e.stopPropagation()}>
      <Box
        as="button"
        type="button"
        aria-label={playing ? "Pause" : "Play voice note"}
        onClick={toggle}
        w={`${h}px`}
        h={`${h}px`}
        flexShrink={0}
        borderRadius="full"
        bg={color}
        color={BG}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow={`0 0 0 6px ${alpha(color, playing ? 0.18 : 0.08)}`}
        transition="box-shadow .3s, transform .2s"
        _hover={{ transform: "scale(1.05)" }}
      >
        <svg viewBox="0 0 20 20" width={h * 0.4} height={h * 0.4} aria-hidden="true">
          {playing ? (
            <g fill="currentColor">
              <rect x="4" y="3" width="4" height="14" rx="1.2" />
              <rect x="12" y="3" width="4" height="14" rx="1.2" />
            </g>
          ) : (
            <path d="M6 3.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L7.5 2.64A1 1 0 0 0 6 3.5z" fill="currentColor" />
          )}
        </svg>
      </Box>
      <Box flex="1" minW={0} cursor={dur ? "pointer" : "default"} onClick={seek}>
        <svg viewBox={`0 0 ${bars.length * 6} 40`} width="100%" height={h * 0.8} preserveAspectRatio="none" aria-hidden="true">
          {bars.map((v, i) => {
            const bh = (v / 9) * 34 + 2;
            const on = (i + 0.5) / bars.length <= p;
            return <rect key={i} x={i * 6 + 1} y={20 - bh / 2} width={3.4} height={bh} rx={1.7} fill={on ? color : alpha(FG, playing ? 0.35 : 0.28)} />;
          })}
        </svg>
      </Box>
      <Text fontFamily={MONO} fontSize="xs" color={playing ? FG : DIM} flexShrink={0} minW="34px" textAlign="right">
        {fmt(playing || t ? t : dur)}
      </Text>
      {src && <audio ref={audio} src={src} preload="metadata" />}
    </Flex>
  );
};

export default VoiceNote;
